/* ==========================================================================
   BYAYA — the sticky bar on the product page (21 oct 2026)
   Markup: snippets/sticky-atc.liquid. Styles: assets/byaya-sticky.css.

   Two things in the bar follow the page:
   - the price: a copy of the price on the page, kept in step with it (the
     theme rewrites that one on every variant change, e.g. "2 Names");
   - the stones: one small stone per position, the stone itself once it is
     chosen, an empty ring with a plus while it is not; when they do not all
     fit beside the price (four stones on a phone), the last place says
     "+2" (and so on) instead. Read from the gemstone selector
     (byaya-stone-selector.js: its `state`, `active` and `swatchUrl`), never
     written to it, and redrawn whenever the selector redraws its bar.
     A tap on the stones opens the drawer at the first stone still missing
     (once all are chosen, as it was), right where the customer is — no
     scroll back up the page; when the drawer closes, the focus goes back to
     the stones.
   The buy button next to them is untouched: locked with "Select options"
   until every stone is chosen, as before (byaya-stone-selector.js).
   ========================================================================== */
(function () {
  'use strict';

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function labelFor(cfg, key) {
    var items = cfg.items || [];
    for (var i = 0; i < items.length; i += 1) {
      if (items[i].k === key) {
        if (items[i].l) return items[i].l;
        return String(items[i].v || '').split('-')[0];
      }
    }
    return '';
  }

  /* ---- price -------------------------------------------------------------- */

  function initPrice(bar) {
    var now = bar.querySelector('.byaya-sticky__now');
    if (!now) return;
    var was = bar.querySelector('.byaya-sticky__was');
    var section = bar.closest('.section-main-product') || document;
    var source = section.querySelector('.product-info__price .price__default') || section.querySelector('.price__default');
    if (!source) return;
    function sync() {
      var cur = source.querySelector('.price__current .js-value');
      var old = source.querySelector('.price__was .js-value');
      var curText = cur ? cur.textContent.trim() : '';
      var oldText = old ? old.textContent.trim() : '';
      if (curText && now.textContent !== curText) now.textContent = curText;
      if (was) {
        if (was.textContent !== oldText) was.textContent = oldText;
        was.hidden = !oldText;
      }
    }
    sync();
    if (window.MutationObserver) new MutationObserver(sync).observe(source, { childList: true, characterData: true, subtree: true });
  }

  /* ---- stones ------------------------------------------------------------- */

  function initStones(bar, el) {
    var btn = bar.querySelector('.byaya-sticky__stones');
    var cfgEl = el.querySelector('.byaya-ss__config');
    var open = el.querySelector('.byaya-ss__open');
    if (!btn || !cfgEl || !open) return false;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return false; }
    var n = Number(cfg.stones) || 1;
    var label = el.querySelector('.byaya-ss__label');
    var verb = label && label.firstChild ? String(label.firstChild.textContent || '').trim() : '';
    if (!verb) verb = 'Choose gemstones';

    function current() {
      var state = Array.isArray(el.state) ? el.state : [];
      var active = Math.max(1, Math.min(n, Number(el.active) || n));
      return { state: state, active: active };
    }

    /* how many stones fit beside the price: the row is as wide as the
       space the buy button leaves, and that changes with its label
       ("Select options", "ADD TO CART") and with the price. When not all
       of them fit, the last place shows "+2" (and so on) instead. */
    var row = btn.parentNode;
    function room(active) {
      var rowW = row ? row.clientWidth : 0;
      if (!rowW) return active > 4 ? 4 : active;
      var price = row.querySelector('.byaya-sticky__price');
      var priceW = price ? price.getBoundingClientRect().width : 0;
      var wide = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
      var size = parseFloat(getComputedStyle(bar).getPropertyValue(wide ? '--byaya-sticky-stone' : '--byaya-sticky-stone-m')) || 24;
      var gap = 5;
      var avail = rowW - (priceW ? priceW + 10 : 0);
      return Math.max(1, Math.floor((avail + gap) / (size + gap)));
    }

    var last = '';
    function render() {
      var c = current();
      var fit = room(c.active);
      var max = c.active <= fit ? c.active : Math.max(0, fit - 1);
      var html = '';
      var names = [];
      for (var i = 0; i < c.active; i += 1) {
        var key = c.state[i];
        names.push(key ? labelFor(cfg, key) : 'empty');
        if (i >= max) continue;
        var img = '';
        if (key && typeof el.swatchUrl === 'function') {
          try { img = el.swatchUrl(i, key); } catch (err) { img = ''; }
        }
        html += '<span class="byaya-sticky__stone' + (key ? '' : ' byaya-sticky__stone--empty') + '"' +
          (img ? ' style="background-image:url(&quot;' + escapeHtml(img) + '&quot;)"' : '') + '></span>';
      }
      if (c.active > max) {
        var restOpen = false;
        for (var j = max; j < c.active; j += 1) if (!c.state[j]) restOpen = true;
        html += '<span class="byaya-sticky__stone byaya-sticky__stone--more' + (restOpen ? ' is-open' : '') + '">+' + (c.active - max) + '</span>';
      }
      var aria = verb + ': ' + names.join(', ');
      var sig = html + '|' + aria;
      if (sig === last) return;
      last = sig;
      btn.innerHTML = html;
      btn.setAttribute('aria-label', aria);
      btn.hidden = false;
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (typeof el.open !== 'function') return;
      var c = current();
      var pos = -1;
      for (var i = 0; i < c.active; i += 1) { if (!c.state[i]) { pos = i; break; } }
      el.open(pos >= 0 ? pos : null);
      // back to the stones once the drawer is closed again
      var drawer = el.drawer;
      if (!drawer || !window.MutationObserver) return;
      var mo = new MutationObserver(function () {
        if (drawer.classList.contains('is-open')) return;
        mo.disconnect();
        try { btn.focus({ preventScroll: true }); } catch (err) { /* an old browser: no focus move */ }
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });

    render();
    // the selector writes the month names and the counter into its bar on
    // every change: redraw then, and on a change in the form (the count
    // option) and after a tap anywhere in the selector
    if (window.MutationObserver) {
      new MutationObserver(render).observe(open, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style'] });
    }
    // and when the room beside the price changes (the button's label, the
    // price, a turned phone)
    if (window.ResizeObserver && row) new ResizeObserver(function () { render(); }).observe(row);
    var form = el.closest('form') || document;
    form.addEventListener('change', function () { setTimeout(render, 0); });
    el.addEventListener('click', function () { setTimeout(render, 50); setTimeout(render, 400); });
    return true;
  }

  /* ---- start -------------------------------------------------------------- */

  function boot() {
    var bars = document.querySelectorAll('sticky-atc.byaya-sticky');
    for (var i = 0; i < bars.length; i += 1) {
      var bar = bars[i];
      if (!bar.__byayaPrice) { bar.__byayaPrice = true; initPrice(bar); }
      if (!bar.__byayaStones && bar.querySelector('.byaya-sticky__stones')) {
        var section = bar.closest('.section-main-product') || document;
        var el = section.querySelector('byaya-stone-selector');
        // the selector reads its config when it starts; until then there is
        // no state to draw from
        if (el && el.cfg && initStones(bar, el)) bar.__byayaStones = true;
      }
    }
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('byaya-stone-selector').then(function () { setTimeout(boot, 0); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  window.addEventListener('load', boot);
  setTimeout(boot, 800);
})();
